import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EmpreenderApps } from "../../files/AppsArray";
import { appsDaCentral } from "../../lib/appsDaCentral";
import { chamarAdmin, mensagemDeErro } from "../../lib/adminApi";

// Aba "Base de conhecimento" (só admins): materiais extras por app (PDF, Word, texto) que a IA da
// central usa junto com os artigos. O arquivo é lido aqui no navegador e só o texto vai para o
// servidor (api/admin-base.ts). Material novo entra desativado: confira o texto e os conflitos e ative.

interface Conflito {
  material: string;
  central: string;
  explicacao: string;
}
interface Material {
  id: string;
  app: string;
  nome: string;
  tipo: string;
  ativo: boolean;
  trechos: number;
  caracteres: number;
  criado_por: string | null;
  criado_em: string;
  ativado_por: string | null;
  ativado_em: string | null;
  conflitos: Conflito[] | null;
}

// Leitores carregados só quando alguém envia um arquivo (versões fixas do cdnjs).
const PDFJS = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
const PDFJS_WORKER = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
const MAMMOTH = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
const carregarScript = (src: string) =>
  new Promise<void>((ok, falha) => {
    if (document.querySelector(`script[src="${src}"]`)) return ok();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => ok();
    s.onerror = () => falha(new Error("leitor"));
    document.head.appendChild(s);
  });

// Texto do arquivo, por tipo. PDF: página por página (PDF escaneado, só imagem, sai vazio).
const lerArquivo = async (arquivo: File): Promise<{ texto: string; tipo: string; paginas?: number }> => {
  const nome = arquivo.name.toLowerCase();
  if (nome.endsWith(".pdf")) {
    await carregarScript(PDFJS);
    const pdfjs = (window as any).pdfjsLib;
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    const pdf = await pdfjs.getDocument({ data: await arquivo.arrayBuffer() }).promise;
    const paginas: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const conteudo = await (await pdf.getPage(i)).getTextContent();
      // Junta os pedaços da linha e quebra parágrafo onde o PDF marca fim de linha com espaço grande.
      let pagina = "";
      for (const item of conteudo.items as { str: string; hasEOL?: boolean }[]) pagina += item.str + (item.hasEOL ? "\n" : "");
      paginas.push(pagina.replace(/\n{3,}/g, "\n\n").trim());
    }
    return { texto: paginas.join("\n\n"), tipo: "pdf", paginas: pdf.numPages };
  }
  if (nome.endsWith(".docx")) {
    await carregarScript(MAMMOTH);
    const r = await (window as any).mammoth.extractRawText({ arrayBuffer: await arquivo.arrayBuffer() });
    return { texto: String(r.value || ""), tipo: "docx" };
  }
  if (nome.endsWith(".txt") || nome.endsWith(".md")) return { texto: await arquivo.text(), tipo: nome.endsWith(".md") ? "md" : "txt" };
  throw new Error("tipo");
};

const nomeDoApp = (slug: string) => EmpreenderApps.find((a) => a.slugamigavel === slug || a.slug === slug)?.name || slug;
const fmtNum = (n: number) => n.toLocaleString("pt-BR");

const AdminBase: React.FC = () => {
  const apps = useMemo(() => appsDaCentral("pt"), []);
  const [materiais, setMateriais] = useState<Material[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [app, setApp] = useState("");
  const [lido, setLido] = useState<{ nome: string; texto: string; tipo: string; paginas?: number } | null>(null);
  const [lendo, setLendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [trechos, setTrechos] = useState<{ ordem: number; texto: string }[] | null>(null);
  const [conferindo, setConferindo] = useState<string | null>(null);

  const listar = useCallback(async () => {
    const r = await chamarAdmin("/api/admin-base", { acao: "listar" });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro, "Não foi possível carregar a base de conhecimento."));
    setMateriais(r.json.materiais as Material[]);
  }, []);

  useEffect(() => {
    listar();
  }, [listar]);

  const escolherArquivo = async (arquivo: File | undefined) => {
    setErro(null);
    setLido(null);
    if (!arquivo) return;
    if (arquivo.size > 30 * 1024 * 1024) return setErro("Arquivo grande demais (máximo de 30 MB).");
    setLendo(true);
    try {
      const r = await lerArquivo(arquivo);
      const texto = r.texto.replace(/\u0000/g, "").trim();
      if (texto.replace(/\s/g, "").length < 200)
        return setErro("Quase nenhum texto foi encontrado no arquivo. Se for um PDF escaneado (só imagem), envie uma versão com texto selecionável.");
      setLido({ nome: arquivo.name.replace(/\.[^.]+$/, ""), texto, tipo: r.tipo, paginas: r.paginas });
    } catch (e) {
      setErro(e instanceof Error && e.message === "tipo" ? "Envie um PDF, Word (.docx) ou texto (.txt, .md)." : "Não foi possível ler o arquivo.");
    } finally {
      setLendo(false);
    }
  };

  const enviar = async () => {
    if (!lido || !app) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await chamarAdmin("/api/admin-base", { acao: "enviar", app, nome: lido.nome, tipo: lido.tipo, texto: lido.texto });
      if (!r.ok) return setErro(mensagemDeErro(r.json?.erro, "Não foi possível enviar o material."));
      setLido(null);
      setAberto(r.json.id);
      await listar();
    } finally {
      setEnviando(false);
    }
  };

  const verTrechos = async (id: string) => {
    if (aberto === id && trechos) return setAberto(null);
    setAberto(id);
    setTrechos(null);
    const r = await chamarAdmin("/api/admin-base", { acao: "trechos", id });
    setTrechos(r.ok ? r.json.trechos : []);
  };

  const ativar = async (m: Material) => {
    if (!m.ativo && !window.confirm(`Ativar "${m.nome}"? A IA da central do ${nomeDoApp(m.app)} passa a usar este material nas respostas.`)) return;
    const r = await chamarAdmin("/api/admin-base", { acao: "ativar", id: m.id, ativo: !m.ativo });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    listar();
  };

  const apagar = async (m: Material) => {
    if (!window.confirm(`Apagar "${m.nome}"? Isso não pode ser desfeito.`)) return;
    const r = await chamarAdmin("/api/admin-base", { acao: "apagar", id: m.id });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    if (aberto === m.id) setAberto(null);
    listar();
  };

  const conferir = async (m: Material) => {
    setConferindo(m.id);
    try {
      const r = await chamarAdmin("/api/admin-base", { acao: "conferir", id: m.id });
      if (!r.ok) return setErro(mensagemDeErro(r.json?.erro, "Não foi possível conferir os conflitos agora."));
      listar();
    } finally {
      setConferindo(null);
    }
  };

  return (
    <>
      <section className="metricas-bloco">
        <h2>Enviar material</h2>
        <p className="metricas-legenda">
          PDF, Word (.docx) ou texto (.txt, .md). O arquivo é lido aqui no navegador e só o texto vai para o servidor. O material entra
          desativado: confira o texto e os conflitos com os artigos antes de ativar. A IA usa o material para explicar conceitos; fatos do app
          (preço, recursos, integrações, prazos) continuam vindo só dos artigos da central.
        </p>
        <div className="base-envio">
          <label>
            App
            <select value={app} onChange={(e) => setApp(e.target.value)}>
              <option value="">Escolha o app</option>
              {apps.map((a) => (
                <option key={a.slugamigavel} value={a.slugamigavel}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Arquivo
            <input type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => escolherArquivo(e.target.files?.[0])} disabled={lendo || enviando} />
          </label>
          {lendo && <span className="metricas-carregando">Lendo o arquivo…</span>}
        </div>
        {lido && (
          <div className="base-previa">
            <label>
              Nome do material
              <input value={lido.nome} maxLength={120} onChange={(e) => setLido({ ...lido, nome: e.target.value })} />
            </label>
            <small>
              {lido.paginas ? `${fmtNum(lido.paginas)} páginas · ` : ""}
              {fmtNum(lido.texto.length)} caracteres de texto. Confira se o começo saiu legível:
            </small>
            <pre>{lido.texto.slice(0, 2500)}</pre>
            <button className="metricas-botao" onClick={enviar} disabled={!app || !lido.nome.trim() || enviando}>
              {enviando ? "Enviando e conferindo conflitos… (pode levar 1 minuto)" : app ? "Enviar (entra desativado)" : "Escolha o app para enviar"}
            </button>
          </div>
        )}
        {erro && <p className="metricas-erro">{erro}</p>}
      </section>

      <section className="metricas-bloco">
        <h2>Materiais</h2>
        {!materiais ? (
          <p className="metricas-vazio">Carregando…</p>
        ) : !materiais.length ? (
          <p className="metricas-vazio">Nenhum material enviado ainda.</p>
        ) : (
          <ol className="metricas-lista base-lista">
            {materiais.map((m) => (
              <li key={m.id}>
                <span className={`metricas-selo ${m.ativo ? "base-ativo" : ""}`}>{m.ativo ? "Ativo" : "Desativado"}</span>
                <div className="metricas-item">
                  <p>
                    <strong>{m.nome}</strong> · {nomeDoApp(m.app)}
                  </p>
                  <small>
                    {m.tipo.toUpperCase()} · {fmtNum(m.trechos)} trechos · enviado por {m.criado_por || "—"} em {m.criado_em}
                    {m.ativo && m.ativado_por && ` · ativado por ${m.ativado_por} em ${m.ativado_em}`}
                  </small>
                  {m.conflitos === null ? (
                    <p className="base-conflitos-vazio">Conflitos ainda não conferidos.</p>
                  ) : m.conflitos.length ? (
                    <div className="base-conflitos">
                      <strong>{m.conflitos.length} ponto(s) para revisar antes de ativar:</strong>
                      <ul>
                        {m.conflitos.map((c, i) => (
                          <li key={i}>
                            <span>Material: {c.material}</span>
                            <span>Central: {c.central}</span>
                            <small>{c.explicacao}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="base-conflitos-vazio">Nenhum conflito com os artigos da central.</p>
                  )}
                  <div className="base-acoes">
                    <button className="metricas-botao" onClick={() => ativar(m)}>
                      {m.ativo ? "Desativar" : "Ativar"}
                    </button>
                    <button className="metricas-link" onClick={() => verTrechos(m.id)}>
                      {aberto === m.id && trechos ? "Esconder o texto" : "Ver o texto em trechos"}
                    </button>
                    <button className="metricas-link" onClick={() => conferir(m)} disabled={conferindo === m.id}>
                      {conferindo === m.id ? "Conferindo…" : "Conferir conflitos de novo"}
                    </button>
                    <button className="metricas-link base-apagar" onClick={() => apagar(m)}>
                      Apagar
                    </button>
                  </div>
                  {aberto === m.id && (
                    <div className="base-trechos">
                      {!trechos ? (
                        <p className="metricas-vazio">Carregando…</p>
                      ) : (
                        trechos.map((t) => (
                          <p key={t.ordem}>
                            <small>#{t.ordem + 1}</small> {t.texto}
                          </p>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
};

export default AdminBase;
